import React, {useState} from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import TopBar from '../../Components/TopBar';
import Page from '../../Components/Containers/Page';
import { useCustomer } from '../../Data/Customers';
import { PageLoading } from '../../Components/Loading';
import {getTitle} from '../../Components/Formatting/Customer/Title';
import Icon from '../../Components/Icons/Icon';
import Address from '../../Components/Formatting/Address';
import NoValue from '../../Components/EmptyStates/NoValue';
import Grid from '../../Components/Formatting/Grid';
import Money from '../../Components/Formatting/Money';
import Tabs, {Tab} from '../../Components/Containers/Tabs/Tabs';
import Badge from '../../Components/Badge/Badge';
import EditCustomer from './EditCustomer';
import Invoices from './Invoices';
import Jobs from './Jobs';
import { usePreference } from '../../Data/Preference';
import { useMapUrl } from '../../Components/Formatting/Map/Map';
import Thread from '../../Components/Threads';
import { useNewWindow, useVisibilityChange } from '../../Services/helpers';
import { useDialogues } from '../../Components/Dialogues';
import HeaderImageAndCard from '../../Components/Containers/HeaderImageAndCard';
import PhoneNumber from '../../Components/Formatting/PhoneNumber';
import Email from '../../Components/Formatting/Email';
import Text from '../../Components/Formatting/Text';
import EditBalance from './EditBalance';
import ChargePaymentMethod from './ChargePaymentMethod';
import { useSession } from '../../Components/Session';
import Button from '../../Components/Button/Button';
import ButtonRow from '../../Components/Button/ButtonRow';
import PortalPin from './PortalPin';
import AddExternalPayment from './AddExternalPayment';
import AddLoyaltyCard from './AddLoyaltyCard';
import CommunicationPreferences from './CommunicationPreferences';

const Customer = () => {

    const {customerId} = useParams();
    const {confirm} = useDialogues();
    const {customer, refresh, updateCommunicationPreferences, addExternalPayment, addLoyaltyCard, cancelLoyaltyCard, editBalance, chargePaymentMethod, deleteCustomer, deleteJob, deleteInvoice, changeSecurity} = useCustomer(customerId, true);
    const [isEditing, setIsEditing] = useState(false);
    const [isEditingBalance, setIsEditingBalance] = useState(false);
    const [isChargingPaymentMethod, setIsChargingPaymentMethod] = useState(false);
    const [isAddingExternalPayment, setIsAddingExternalPayment] = useState(false);
    const [isAddingLoyaltyCard, setIsAddingLoyaltyCard] = useState(false);
    const [isUpdatingCommsPrefs, setIsUpdatingCommsPrefs] = useState(false);
    const [viewingPin, setViewingPin] = useState(false);
    const navigate = useNavigate();
    const newWindow = useNewWindow();
    const {hasFeature} = useSession();
    const {featureUnavailable} = useDialogues();

    useVisibilityChange(refresh);

    return <>
        <TopBar
            left="BackBtn"
            title={getTitle(customer)}
            dropdownOptions={[
                {
                    title: 'Add external payment',
                    icon: 'MoneyBillWave',
                    onClick: () => setIsAddingExternalPayment(true)
                },
                {
                    title: 'Charge customer',
                    icon: 'CreditCardFront',
                    onClick: () => setIsChargingPaymentMethod(true)
                },
                {
                    title: 'Edit balance',
                    icon: 'PiggyBank',
                    onClick: () => setIsEditingBalance(true)
                },
                {
                    div: true,
                },
                {
                    title: 'Edit customer',
                    icon: 'Pencil',
                    onClick: () => setIsEditing(true)
                },
                {
                    title: 'Communications',
                    icon: 'PaperPlane',
                    onClick: () => setIsUpdatingCommsPrefs(true)
                },
                {
                    title: 'Add loyalty card',
                    icon: 'GiftCard',
                    onClick: () => {
                        hasFeature('LOYALTY_CARDS') ? setIsAddingLoyaltyCard(true) : featureUnavailable('LOYALTY_CARDS');
                    },
                },
                {
                    div: true,
                },
                {
                    title: 'View customer portal',
                    icon: 'LinkExternal',
                    onClick: () => {
                        if (hasFeature('CUSTOMER_PORTAL')) {
                            newWindow(customer?.portalUrl);
                            return;
                        }

                        confirm(
                            {
                                theme: 'info',
                                title: 'Customer portal unavailable',
                                message: <>
                                    The customer portal is not available on your current plan.<br />
                                    Customers will instead be directed to the payments page.
                                </>,
                                confirmLabel: 'Payments page'
                            },
                            () => newWindow(customer?.portalUrl),
                        )
                    }
                },
                {
                    title: 'View PIN',
                    icon: 'Lock',
                    onClick: () => {
                        hasFeature('CUSTOMER_PORTAL') ? setViewingPin(true) : featureUnavailable('CUSTOMER_PORTAL');
                    }
                },
                {
                    div: true,
                },
                {
                    title: 'Delete customer',
                    icon: 'Trash',
                    onClick: () => confirm(
                        {theme: 'danger', title: 'Delete customer', message: 'Are you sure you want to delete this customer?'},
                        () => deleteCustomer().then(() => navigate('/manage/customers'))
                    )
                }
            ]}
        />
        <Page zeroPad>
            {customer ? <Content {...{customer, refresh, deleteJob, deleteInvoice, cancelLoyaltyCard}} /> : <PageLoading />}
        </Page>

        <EditCustomer customerId={customerId}
            customer={customer}
            isOpen={isEditing}
            onClose={() => setIsEditing(false)}
            onSave={refresh}
        />

        <EditBalance customer={customer}
            isOpen={isEditingBalance}
            setIsOpen={() => setIsEditingBalance(false)}
            editBalance={editBalance}
        />

        <AddExternalPayment customer={customer}
            isOpen={isAddingExternalPayment}
            setIsOpen={() => setIsAddingExternalPayment(false)}
            addExternalPayment={addExternalPayment}
        />

        <CommunicationPreferences customer={customer}
            isOpen={isUpdatingCommsPrefs}
            setIsOpen={() => setIsUpdatingCommsPrefs(false)}
            updateCommunicationPreferences={updateCommunicationPreferences}
        />

        <AddLoyaltyCard customer={customer}
            isOpen={isAddingLoyaltyCard}
            setIsOpen={() => setIsAddingLoyaltyCard(false)}
            addLoyaltyCard={addLoyaltyCard}
        />

        <ChargePaymentMethod customer={customer}
            isOpen={isChargingPaymentMethod}
            setIsOpen={() => setIsChargingPaymentMethod(false)}
            chargePaymentMethod={chargePaymentMethod}
        />

        <PortalPin isOpen={viewingPin}
            onClose={() => setViewingPin(false)}
            customer={customer}
            changeSecurity={changeSecurity}
        />
    </>
}

const Content = ({customer, refresh, deleteJob, deleteInvoice, cancelLoyaltyCard}) => {
    const mapUrl = useMapUrl(customer, true, false);
    const iconStyles = {className: "fill-light", size: "1x", inline: true};
    const [selectedTab, setSelectedTab] = usePreference('customerTab', 'Jobs');
    const {hasFeature} = useSession();

    return <>
        <HeaderImageAndCard image={mapUrl}>
            <Grid className="mb-4 lg:max-w-[75%] lg:mx-auto" items={[
                [
                    <Icon icon="User" {...iconStyles} />,
                    <strong>{customer.name ?? <NoValue />}</strong>,
                    !!customer.name
                ],
                [
                    <Icon icon="LocationPin" {...iconStyles} />,
                    customer.address ? <Address address={customer.address} area={customer.area} distance={customer.distance} /> : <NoValue />,
                    !!customer.address
                ],
                [
                    <Icon icon="Phone" {...iconStyles} />,
                    <PhoneNumber phoneNumber={customer.phoneNumber} />,
                    !!customer.phoneNumber
                ],
                [
                    <Icon icon="PaperPlane" {...iconStyles} />,
                    <Email email={customer.email} />,
                    !!customer.email
                ],
                [
                    <Icon icon="SterlingSign" {...iconStyles} />,
                    <Money pence={customer.balance} colour />,
                ],
                [
                    <Icon icon="Note" {...iconStyles} />,
                    <Text text={customer.notes} />,
                    !!customer.notes,
                ],
                [
                    <Icon icon="Calendar" {...iconStyles} />,
                    <span className="text-muted">
                        <Money pence={customer.annualValue} />
                        &nbsp;
                        annual value
                    </span>,
                    !!customer.annualValue,
                ]
            ]} />
        </HeaderImageAndCard>

        <div className="page-padding">
            <Tabs selected={selectedTab} onChange={setSelectedTab}>
                <Tab title="Finances" icon="FileLines">
                    <Invoices {...{customer, deleteInvoice, cancelLoyaltyCard}} />
                </Tab>
                <Tab title="Jobs" icon="Truck">
                    <Jobs {...{customer, refresh, deleteJob}} />
                </Tab>
                <Tab title="Messages" icon="Comment" badge={customer.unreadMessages > 0 && <Badge theme="danger" title={customer.unreadMessages} className="ml-2" />}>
                    {hasFeature('CUSTOMER_PORTAL') ? <Thread threadId={customer.threadId} /> : <CustomerPortalUnavailable />}
                </Tab>
            </Tabs>
        </div>
    </>
}

const CustomerPortalUnavailable = () => {
    const {isRoleSufficient} = useSession();

    return <>
        <p className="text-center text-muted my-5 max-w-[75%] mx-auto">
            Customer messaging is only available via the customer portal, which is not included in your current plan.
        </p>
        <ButtonRow arrange="center">
            <Button link="/billing/plan" theme="primary" label="Upgrade plan" disabled={!isRoleSufficient('Owner')} />
        </ButtonRow>
    </>
}


export default Customer;
